<template>
  <div class="fullscreen-modal" v-if="visible && item">
    <div class="modal-wrapper">
      <!-- Header -->
      <div class="modal-header-custom">
        <button type="button" class="btn-back" @click="close">
          <i class="bx bx-arrow-back"></i>
        </button>
        <span class="title">Registrar retorno</span>
        <div class="spacer"></div>
      </div>

      <!-- Content -->
      <div class="modal-content-custom">
        <!-- Code Badge -->
        <div class="code-badge">
          <span>#{{ item.code }}</span>
        </div>

        <!-- Item Photo -->
        <div class="item-photo-container">
          <div class="item-photo">
            <img v-if="item.photo_url" :src="item.photo_url" alt="Foto do item" />
            <div v-else class="no-photo">
              <i class="bx bx-image"></i>
            </div>
          </div>
        </div>

        <!-- Type Grid -->
        <div class="type-grid">
          <div
            v-for="type in displayTypes"
            :key="type.id"
            class="type-item"
            :class="{ 'selected': isSelectedType(type) }"
          >
            <i :class="type.icon || 'bx bx-package'"></i>
            <span>{{ type.name }}</span>
          </div>
        </div>

        <!-- Item Name (when type is "outro") -->
        <div class="item-name-display" v-if="isOutroType && item.custom_type_name">
          <span class="name-label">Nome:</span>
          <span class="name-value">{{ item.custom_type_name }}</span>
        </div>

        <!-- Info Cards -->
        <div class="info-cards">
          <div class="info-card">
            <div class="icon-box blue">
              <i class="bx bx-map"></i>
            </div>
            <div class="info-content">
              <span class="label">Localiza</span>
              <span class="value">{{ item.location ? item.location.name : '--' }}</span>
            </div>
          </div>

          <div class="info-card">
            <div class="icon-box green">
              <i class="bx bx-time"></i>
            </div>
            <div class="info-content">
              <span class="label">Horario de entrada</span>
              <span class="value">{{ formatDateTime(item.stored_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer-custom">
        <!-- Bluetooth status -->
        <div class="bt-status" v-if="btSupported">
          <div class="bt-indicator" :class="{ connected: btConnected }">
            <i class="bx bx-bluetooth"></i>
            <span v-if="btConnected">{{ btDeviceName }}</span>
            <span v-else>Impressora desconectada</span>
          </div>
          <button type="button" class="btn-bt-connect" @click="connectPrinter" v-if="!btConnected" :disabled="btConnecting">
            <b-spinner small v-if="btConnecting"></b-spinner>
            <span v-else>Conectar</span>
          </button>
          <button type="button" class="btn-bt-disconnect" @click="disconnectPrinter" v-else>
            <i class="bx bx-x"></i>
          </button>
        </div>

        <button type="button" class="btn-confirm" @click="confirmReturn" :disabled="loading">
          <b-spinner small v-if="loading"></b-spinner>
          <template v-else>
            <i class="bx bx-check"></i>
            Confirmar retorno
          </template>
        </button>
        <button type="button" class="btn-print" @click="printLabels">
          <i class="bx bx-printer"></i>
          Reimprimir etiquetas
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { STORAGE_MODULE } from '@/modules/storage'
import { successMessage, errorMessage } from '@/components/alertsMessages'
// import bluetoothPrinter from '@/modules/storage/service/bluetoothPrinter' // antigo (via app nativo)
import bluetoothPrinter from '@/modules/storage/service/webBluetoothPrinter'

export default {
  name: 'return-modal',
  props: {
    item: {
      type: Object,
      default: null
    },
    participant: {
      type: Object,
      default: null
    },
    itemTypes: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: function () {
    return {
      visible: false,
      loading: false,
      btConnecting: false
    }
  },
  computed: {
    displayTypes: function () {
      if (this.itemTypes && this.itemTypes.length > 0) {
        return this.itemTypes
      }
      return []
    },
    isOutroType: function () {
      if (!this.item) return false
      // Se tem storage_item_type_id null, é tipo custom ("outro")
      if (!this.item.storage_item_type_id) return true
      // Verifica se o tipo é "Outro" pelo nome
      var itemType = this.itemTypes.find(function (t) { return t.id === this.item.storage_item_type_id }.bind(this))
      return itemType && itemType.name && itemType.name.toLowerCase() === 'outro'
    },
    btSupported: function () {
      return bluetoothPrinter.isSupported()
    },
    btConnected: function () {
      return bluetoothPrinter.isConnected()
    },
    btDeviceName: function () {
      return bluetoothPrinter.getDeviceName() || 'Impressora'
    }
  },
  methods: {
    ...mapActions(STORAGE_MODULE, ['returnStoredItem']),
    open: function () {
      this.visible = true
      document.body.style.overflow = 'hidden'
    },
    close: function () {
      this.visible = false
      this.loading = false
      document.body.style.overflow = ''
      this.$emit('close')
    },
    formatDateTime: function (dateStr) {
      if (!dateStr) return '--'
      var date = new Date(dateStr)
      var options = { month: 'short', day: 'numeric' }
      var datePart = date.toLocaleDateString('en-US', options)
      var h = String(date.getHours()).padStart(2, '0')
      var min = String(date.getMinutes()).padStart(2, '0')
      return datePart + ', ' + h + ':' + min
    },
    isSelectedType: function (type) {
      if (!this.item) return false
      // Compara pelo ID do tipo
      if (this.item.storage_item_type_id) {
        return type.id === this.item.storage_item_type_id
      }
      // Se não tem type_id, é custom - destaca "Outro"
      return type.name && type.name.toLowerCase() === 'outro'
    },
    confirmReturn: function () {
      var self = this
      if (!self.item) return

      self.loading = true

      self.returnStoredItem({ id: self.item.id, notes: null })
        .then(function (result) {
          self.loading = false
          successMessage(result.message || 'Item devolvido com sucesso!')
          self.$emit('returned', result.data)
        })
        .catch(function (error) {
          self.loading = false
          errorMessage(error.message || 'Erro ao registrar devolucao')
        })
    },
    connectPrinter: function () {
      var self = this
      self.btConnecting = true
      bluetoothPrinter.connect()
        .then(function () {
          self.btConnecting = false
          self.$forceUpdate()
          successMessage('Impressora conectada: ' + bluetoothPrinter.getDeviceName())
        })
        .catch(function (err) {
          self.btConnecting = false
          errorMessage(err.message || 'Erro ao conectar impressora')
        })
    },
    disconnectPrinter: function () {
      bluetoothPrinter.disconnect()
      this.$forceUpdate()
    },
    formatDateTimePrint: function (dateStr) {
      if (!dateStr) return '--'
      var date = new Date(dateStr)
      var dd = String(date.getDate()).padStart(2, '0')
      var mm = String(date.getMonth() + 1).padStart(2, '0')
      var yyyy = date.getFullYear()
      var hh = String(date.getHours()).padStart(2, '0')
      var mi = String(date.getMinutes()).padStart(2, '0')
      return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi
    },
    ensureConnected: function () {
      var self = this
      if (bluetoothPrinter.isConnected()) {
        return Promise.resolve(true)
      }
      return bluetoothPrinter.connect()
        .then(function () {
          self.$forceUpdate()
          return true
        })
    },
    printLabels: function () {
      var self = this
      if (!self.item) return

      // Sem impressora Bluetooth conectada, cai na impressão do navegador —
      // é o caminho de quem opera pelo celular, onde o Web Bluetooth não existe.
      if (!bluetoothPrinter.isSupported() || !bluetoothPrinter.isConnected()) {
        self.openPrintWindow(self.item)
        return
      }

      var typeName = self.item.type_name || self.item.custom_type_name || 'Item'
      var participantName = self.participant ? self.participant.name : ''

      bluetoothPrinter.printStorageLabel({
        code: self.item.code,
        typeName: typeName,
        participantName: participantName,
        dateTime: self.formatDateTimePrint(self.item.stored_at)
      })
        .then(function () {
          successMessage('Etiquetas impressas com sucesso!')
        })
        .catch(function (err) {
          errorMessage('Erro na impressão: ' + (err.message || '') + ' Abrindo a impressão do navegador.')
          self.openPrintWindow(self.item)
        })
    },
    /**
     * Impressão pelo navegador: monta as duas etiquetas (50mm x 30mm) num iframe
     * oculto e chama o diálogo de impressão do aparelho.
     */
    openPrintWindow: function (item) {
      var self = this
      var typeName = item.type_name || item.custom_type_name || 'Item'
      var participantName = self.participant ? self.participant.name : ''
      var dateTime = self.formatDateTimePrint(item.stored_at)

      var printContent = '<!DOCTYPE html><html><head><title>Etiquetas - ' + item.code + '</title>' +
        '<style>@page { size: 50mm 30mm; margin: 0; } body { margin: 0; padding: 0; font-family: Arial, sans-serif; } ' +
        '.label { width: 50mm; height: 30mm; padding: 2mm; box-sizing: border-box; page-break-after: always; border: 1px dashed #ccc; } ' +
        '.label:last-child { page-break-after: auto; } .code { font-size: 14pt; font-weight: bold; text-align: center; margin-bottom: 1mm; } ' +
        '.type { font-size: 8pt; text-align: center; margin-bottom: 1mm; } ' +
        '.participant { font-size: 7pt; text-align: center; color: #666; } .date { font-size: 6pt; text-align: center; color: #999; margin-top: 1mm; }' +
        '</style></head><body>' +
        '<div class="label"><div class="code">' + item.code + '</div><div class="type">' + typeName + '</div>' +
        '<div class="participant">' + participantName + '</div>' +
        '<div class="date">' + dateTime + '</div></div>' +
        '<div class="label"><div class="code">' + item.code + '</div><div class="type">' + typeName + '</div>' +
        '<div class="participant">' + participantName + '</div>' +
        '<div class="date">' + dateTime + '</div></div>' +
        '</body></html>'

      var existingFrame = document.getElementById('storage-print-frame')
      if (existingFrame) existingFrame.remove()

      var iframe = document.createElement('iframe')
      iframe.id = 'storage-print-frame'
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = 'none'
      document.body.appendChild(iframe)

      var doc = iframe.contentDocument || iframe.contentWindow.document
      doc.open()
      doc.write(printContent)
      doc.close()

      setTimeout(function () {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
        setTimeout(function () { iframe.remove() }, 1000)
      }, 250)
    }
  }
}
</script>

<style lang="scss" scoped>
.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 1052;
  display: flex;
  flex-direction: column;
}

.modal-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
}

.modal-header-custom {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;

  .btn-back {
    background: none;
    border: none;
    font-size: 22px;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 32px;

    &:hover {
      color: #333;
    }
  }

  .title {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .spacer {
    width: 32px;
  }
}

.modal-content-custom {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.code-badge {
  text-align: center;
  margin-bottom: 12px;

  span {
    display: inline-block;
    background: #556ee6;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    padding: 6px 20px;
    border-radius: 16px;
  }
}

.item-photo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.item-photo {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .no-photo {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ccc;

    i {
      font-size: 48px;
    }
  }
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.item-name-display {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  .name-label {
    font-size: 12px;
    color: #666;
  }

  .name-value {
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
}

.type-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 4px;
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: default;

  &.selected {
    background: #fff;
    border-color: #556ee6;

    i {
      color: #556ee6;
    }

    span {
      color: #556ee6;
      font-weight: 600;
    }
  }

  i {
    font-size: 20px;
    color: #74788d;
    margin-bottom: 4px;
  }

  span {
    font-size: 10px;
    color: #74788d;
    text-align: center;
  }
}

.info-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;

  .icon-box {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.blue {
      background: rgba(85, 110, 230, 0.1);
      i {
        color: #556ee6;
      }
    }

    &.green {
      background: rgba(52, 195, 143, 0.1);
      i {
        color: #34c38f;
      }
    }

    i {
      font-size: 18px;
    }
  }

  .info-content {
    display: flex;
    flex-direction: column;

    .label {
      font-size: 10px;
      color: #999;
    }

    .value {
      font-size: 13px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }
}

.modal-footer-custom {
  padding: 12px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;

  .bt-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 10px;

    .bt-indicator {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: #999;

      i {
        font-size: 16px;
      }

      &.connected {
        color: #34c38f;

        i {
          color: #34c38f;
        }
      }
    }

    .btn-bt-connect {
      background: #556ee6;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 500;
      cursor: pointer;

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
    }

    .btn-bt-disconnect {
      background: none;
      border: none;
      color: #dc3545;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    }
  }

  .btn-confirm {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: #34c38f;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 8px;

    &:hover:not(:disabled) {
      background: #2eb67d;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    i {
      font-size: 18px;
    }
  }

  .btn-print {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: #74788d;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: #5d6071;
    }

    i {
      font-size: 16px;
    }
  }
}
</style>
